import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { formatPrice } from "../Utils/product";
import "../css/paysuccess.css";

const LAST_ORDER_KEY = "sickzuno:lastOrder";

const createLocalOrderNumber = () => {
  const timestamp = Date.now().toString(36).slice(-6).toUpperCase();
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `SJ-${timestamp}-${suffix}`;
};

const restoreOrder = (routeState) => {
  if (routeState?.orderNumber) return routeState;

  try {
    const savedOrder = window.sessionStorage.getItem(LAST_ORDER_KEY);
    const parsedOrder = savedOrder ? JSON.parse(savedOrder) : null;

    if (parsedOrder?.orderNumber) return parsedOrder;
  } catch (error) {
    console.warn("Unable to restore the order confirmation.", error);
  }

  return {
    orderNumber: createLocalOrderNumber(),
  };
};

const scrollToTop = () =>
  window.scrollTo({ top: 0, behavior: "smooth" });

const PaySuccess = () => {
  const location = useLocation();
  const [order] = useState(() => restoreOrder(location.state));
  const hasTotal = Number.isFinite(Number(order.total));
  const paymentState = new URLSearchParams(location.search).get("state");
  const isCancelled = paymentState === "cancel";

  return (
    <main className="pay-success-page" id="main-content">
      <section className="pay-success-card" aria-labelledby="success-title">
        <div className="pay-success-card__mark" aria-hidden="true">
          <span />
        </div>
        <span className="pay-success-card__eyebrow">
          {isCancelled ? "Payment cancelled" : "Payment confirmed"}
        </span>
        <h1 id="success-title">
          {isCancelled ? "Your payment was not completed" : "Thank you for your order"}
        </h1>
        <p className="pay-success-card__intro">
          {isCancelled
            ? "No completed payment was recorded. You can return to your cart and try again when ready."
            : "Your payment was successful. Keep the order reference below for your records."}
        </p>

        <div className="pay-success-reference">
          <span>Order number</span>
          <strong>{order.orderNumber}</strong>
          {hasTotal && <small>Order total: {formatPrice(Number(order.total))}</small>}
        </div>

        <div className="pay-success-status" aria-label="Order status">
          <article>
            <span>01</span>
            <div>
              <h2>{isCancelled ? "Order retained" : "Order received"}</h2>
              <p>{isCancelled ? "Your cart remains available for another attempt." : "We have received your order details."}</p>
            </div>
          </article>
          <article>
            <span>02</span>
            <div>
              <h2>{isCancelled ? "No charge completed" : "Payment confirmed"}</h2>
              <p>{isCancelled ? "The payment flow was cancelled before completion." : "The payment provider returned a successful status."}</p>
            </div>
          </article>
          <article>
            <span>03</span>
            <div>
              <h2>{isCancelled ? "Ready when you are" : "Preparing for delivery"}</h2>
              <p>{isCancelled ? "Return to checkout whenever you want to continue." : "Delivery updates will be sent to the email used at checkout."}</p>
            </div>
          </article>
        </div>

        <div className="pay-success-note" role="note">
          <strong>{isCancelled ? "Payment cancelled." : "Payment successful."}</strong>
          <span>
            {isCancelled ? "You can safely return to your cart." : "Thank you for shopping with sickzuno."}
          </span>
        </div>

        <div className="pay-success-actions">
          <Link
            className="pay-success-button pay-success-button--primary"
            to="/shop"
            onClick={scrollToTop}
          >
            Continue shopping
          </Link>
          <Link
            className="pay-success-button pay-success-button--secondary"
            to="/"
            onClick={scrollToTop}
          >
            Return home
          </Link>
        </div>
      </section>
    </main>
  );
};

export default PaySuccess;
